import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { useSubscriptionStore } from '@/store/subscription';
import { track } from '@/lib/posthog';

export function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const { fetchSubscription } = useSubscriptionStore();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyCheckout = async () => {
      if (!sessionId) {
        setStatus('error');
        setError('No checkout session found');
        return;
      }

      try {
        // Give Stripe webhook a moment to process
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Fetch the subscription to verify it was created
        await fetchSubscription();

        track('trial_started', { stripe_session_id: sessionId });
        setStatus('success');
      } catch (err) {
        console.error('Error verifying checkout:', err);
        setStatus('error');
        setError('Failed to verify your subscription. Please contact support.');
      }
    };

    verifyCheckout();
  }, [sessionId, fetchSubscription]);

  const handleContinue = () => {
    navigate('/onboarding');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="py-16">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 text-ingesta-blue-500 animate-spin" />
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Setting up your subscription...
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will only take a moment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Something went wrong
            </CardTitle>
            <CardDescription className="text-base">
              {error || 'We couldn\'t verify your subscription.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Don't worry - if your payment was successful, your subscription will be activated shortly.
              You can continue to set up your account.
            </p>
            <Button className="w-full" onClick={handleContinue}>
              Continue Setup
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-ingesta-blue-600">
            You're all set!
          </CardTitle>
          <CardDescription className="text-base">
            Your 7-day free trial has started.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-800">
                <Check className="w-4 h-4" />
                <span className="font-medium">Trial active</span>
              </div>
              <p className="text-sm text-green-700">
                Enjoy full Pro access for 7 days. You won't be charged until your trial ends.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">What's included:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Unlimited projects
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                AI-powered document analysis
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Automatic project extraction
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                Cancel anytime
              </li>
            </ul>
          </div>

          <div className="pt-4 border-t">
            <Button className="w-full" size="lg" onClick={handleContinue}>
              Continue Setup
            </Button>
            <p className="text-xs text-center text-gray-500 mt-3">
              Next: Set up your profile and create your first workspace
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
